import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import { 
  Crown, Users, Copy, Share2, Plus, LogIn, Loader2, ChevronRight, 
  Trophy, X, Check, Globe, Lock, Settings, Search, ExternalLink,
  ShoppingCart, AlertCircle
} from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import {
  initializeRevenueCat,
  getOfferings,
  purchasePackage,
  isNativePlatform,
} from "../utils/revenuecat";
import { setAdBannerVisible } from "../components/AdBanner";
import EngagementLockModal, { useEngagementStatus } from "../components/EngagementLockModal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// League purchase product IDs (v2 versions for Apple)
const LEAGUE_PRODUCTS = {
  private: "apl_league_private_v2",
  public: "apl_league_public_v2",
  size_15: "apl_league_size_15_v2",
  size_30: "apl_league_size_30_v2",
  size_50: "apl_league_size_50_v2",
};

export default function LeaguesPage() {
  const { user, dbUser, getAuthHeader } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // My leagues state
  const [myLeagues, setMyLeagues] = useState([]);
  const [slots, setSlots] = useState({ create_used: 0, create_available: 1, join_used: 0, join_limit: 5 });
  
  // Public leagues state
  const [publicLeagues, setPublicLeagues] = useState([]);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("members");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalPublic, setTotalPublic] = useState(0);
  
  // Loading states
  const [loadingMy, setLoadingMy] = useState(true);
  const [loadingPublic, setLoadingPublic] = useState(true);
  
  // Messages
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLeague, setPreviewLeague] = useState(null);
  const [showEngagementModal, setShowEngagementModal] = useState(false);
  
  // Engagement gate (season team + daily team)
  const engagement = useEngagementStatus();
  
  // Form states
  const [newLeagueName, setNewLeagueName] = useState("");
  const [newLeagueVisibility, setNewLeagueVisibility] = useState("private");
  const [newLeagueDescription, setNewLeagueDescription] = useState("");
  const [joinCode, setJoinCode] = useState("");
  const [creating, setCreating] = useState(false);
  const [joining, setJoining] = useState(false);
  const [requestingId, setRequestingId] = useState(null);
  
  // RevenueCat state
  const [rcInitialized, setRcInitialized] = useState(false);
  const [offerings, setOfferings] = useState(null);
  const [purchasing, setPurchasing] = useState(null);
  const [isNative, setIsNative] = useState(false);

  // Hide ad banner when any modal is open (native layer conflict)
  useEffect(() => {
    const anyModalOpen = showCreateModal || showJoinModal || showUpgradeModal || showPreviewModal || showEngagementModal;
    setAdBannerVisible(!anyModalOpen);
  }, [showCreateModal, showJoinModal, showUpgradeModal, showPreviewModal, showEngagementModal]);

  // Initialize RevenueCat
  useEffect(() => {
    const initRC = async () => {
      setIsNative(isNativePlatform());
      if (!isNativePlatform() || !user) return;
      
      try {
        const initialized = await initializeRevenueCat(user.uid);
        setRcInitialized(initialized);
        if (initialized) {
          const off = await getOfferings();
          setOfferings(off);
        }
      } catch (err) {
        console.error("RevenueCat init error:", err);
      }
    };
    initRC();
  }, [user]);

  // Fetch my leagues
  const fetchMyLeagues = useCallback(async () => {
    if (!user) {
      setLoadingMy(false);
      return;
    }
    
    try {
      const res = await axios.get(`${API}/leagues/my-leagues`, { headers: getAuthHeader() });
      setMyLeagues(res.data.leagues || []);
      setSlots(res.data.slots || { create_used: 0, create_available: 1, join_used: 0, join_limit: 5 });
    } catch (e) {
      console.error("Failed to fetch my leagues:", e);
    } finally {
      setLoadingMy(false);
    }
  }, [user, getAuthHeader]);

  // Fetch public leagues
  const fetchPublicLeagues = useCallback(async () => {
    setLoadingPublic(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: "10",
        sort
      });
      if (search) params.append("search", search);
      
      const res = await axios.get(`${API}/leagues/public?${params.toString()}`);
      setPublicLeagues(res.data.leagues || []);
      setTotalPages(res.data.pages || 1);
      setTotalPublic(res.data.total || 0);
    } catch (e) {
      console.error("Failed to fetch public leagues:", e);
    } finally {
      setLoadingPublic(false);
    }
  }, [page, sort, search]);

  useEffect(() => {
    fetchMyLeagues();
    fetchPublicLeagues();
  }, [fetchMyLeagues, fetchPublicLeagues]);

  // Handle deep link ?join= parameter
  useEffect(() => {
    const joinParam = searchParams.get("join");
    if (joinParam && user) {
      setJoinCode(joinParam.toUpperCase());
      setShowJoinModal(true);
      // Clear the URL param
      searchParams.delete("join");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, user, setSearchParams]);

  const handleRefresh = async () => {
    await Promise.all([fetchMyLeagues(), fetchPublicLeagues()]);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchPublicLeagues();
  };

  const handleCreateClick = () => {
    if (!user) {
      navigate("/auth", { state: { returnTo: "/leagues" } });
      return;
    }
    if (slots.create_used >= slots.create_available) {
      setShowUpgradeModal(true);
    } else {
      setShowCreateModal(true);
    }
  };

  const createLeague = async (e) => {
    e.preventDefault();
    if (!newLeagueName.trim()) {
      setError("Enter a league name");
      return;
    }

    setCreating(true);
    setError("");
    
    try {
      const res = await axios.post(`${API}/leagues`, 
        { 
          name: newLeagueName.trim(), 
          max_members: 5,
          visibility: newLeagueVisibility,
          description: newLeagueDescription.trim() || null
        },
        { headers: getAuthHeader() }
      );
      setSuccess(`League created! Code: ${res.data.code}`);
      setNewLeagueName("");
      setNewLeagueDescription("");
      setNewLeagueVisibility("private");
      setShowCreateModal(false);
      fetchMyLeagues();
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create league");
    } finally {
      setCreating(false);
    }
  };

  const joinLeague = async (e) => {
    e.preventDefault();
    if (!joinCode.trim()) {
      setError("Enter a league code");
      return;
    }
    if (engagement.loaded && (!engagement.hasSeasonTeam || !engagement.hasDailyTeam)) {
      setShowJoinModal(false);
      setShowEngagementModal(true);
      return;
    }

    setJoining(true);
    setError("");
    
    try {
      const res = await axios.post(`${API}/leagues/join/${joinCode.trim().toUpperCase()}`, {}, { headers: getAuthHeader() });
      setSuccess(`Joined ${res.data.league_name}!`);
      setJoinCode("");
      setShowJoinModal(false);
      fetchMyLeagues();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.code === "ENGAGEMENT_REQUIRED") {
        engagement.applyBackendStatus(detail);
        setShowJoinModal(false);
        setShowEngagementModal(true);
      } else {
        setError(typeof detail === "string" ? detail : (detail?.message || "Failed to join league"));
      }
    } finally {
      setJoining(false);
    }
  };

  const requestToJoin = async (leagueId, leagueName) => {
    if (!user) {
      navigate("/auth", { state: { returnTo: "/leagues" } });
      return;
    }
    if (engagement.loaded && (!engagement.hasSeasonTeam || !engagement.hasDailyTeam)) {
      setShowPreviewModal(false);
      setPreviewLeague(null);
      setShowEngagementModal(true);
      return;
    }

    setRequestingId(leagueId);
    setError("");
    setSuccess("");

    try {
      await axios.post(`${API}/leagues/${leagueId}/request-join`, {}, { headers: getAuthHeader() });
      setSuccess(`Join request sent for "${leagueName}". The organizer will review it.`);
      setShowPreviewModal(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.code === "ENGAGEMENT_REQUIRED") {
        engagement.applyBackendStatus(detail);
        setShowPreviewModal(false);
        setPreviewLeague(null);
        setShowEngagementModal(true);
      } else {
        setError(typeof detail === "string" ? detail : (detail?.message || "Failed to send join request"));
      }
    } finally {
      setRequestingId(null);
    }
  };

  const handleLeagueClick = (league) => {
    // Check if user is already a member
    const isMember = user && myLeagues.some(l => l.id === league.id);
    
    if (isMember) {
      navigate(`/my-leagues/${league.id}`);
      return;
    }
    // Gate: non-engaged users see engagement prompt instead of league preview
    if (user && engagement.loaded && (!engagement.hasSeasonTeam || !engagement.hasDailyTeam)) {
      setShowEngagementModal(true);
      return;
    }
    // Show preview modal
    setPreviewLeague(league);
    setShowPreviewModal(true);
  };

  // RevenueCat purchase handler
  const handlePurchase = async (productType) => {
    if (!rcInitialized || !offerings?.current) {
      setError("Purchases not available. Please try again later.");
      return;
    }
    
    setPurchasing(productType);
    setError("");
    
    try {
      const productId = LEAGUE_PRODUCTS[productType];
      const pkg = offerings.current.availablePackages.find(
        p => p.identifier === productId || p.product.identifier === productId
      );
      
      if (!pkg) {
        setError("Product not found. Please contact support.");
        setPurchasing(null);
        return;
      }
      
      const result = await purchasePackage(pkg);
      
      if (result.cancelled) {
        setPurchasing(null);
        return;
      }
      
      if (result.success) {
        // Record purchase in backend
        await axios.post(`${API}/leagues/purchase`, {
          product_id: productId,
          transaction_id: result.productIdentifier
        }, { headers: getAuthHeader() });
        
        setSuccess(`Purchase successful! You can now create more leagues.`);
        setShowUpgradeModal(false);
        fetchMyLeagues();
      }
    } catch (err) {
      console.error("Purchase error:", err);
      setError(err.message || "Purchase failed. Please try again.");
    } finally {
      setPurchasing(null);
    }
  };

  const shareWhatsApp = (code, name) => {
    const message = `Join my APL Mini League!\n\nLeague: *${name}*\nCode: *${code}*\n\nJoin here: ${window.location.origin}/leagues?join=${code}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setSuccess(`Code ${code} copied!`);
    setTimeout(() => setSuccess(""), 2000);
  };

  return (
    <div>
    <div className="space-y-6 pb-8" data-testid="leagues-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Leagues</h1>
          <p className="text-slate-400 text-sm">Create, join, and compete</p>
        </div>
        <Crown className="text-amber-400" size={28} />
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 text-red-400 rounded-lg p-3 text-sm flex items-center gap-2">
          <AlertCircle size={16} />
          {error}
          <button onClick={() => setError("")} className="ml-auto"><X size={16} /></button>
        </div>
      )}
      {success && (
        <div className="bg-emerald-500/20 border border-emerald-500/50 text-emerald-400 rounded-lg p-3 text-sm flex items-center gap-2">
          <Check size={16} />
          {success}
          <button onClick={() => setSuccess("")} className="ml-auto"><X size={16} /></button>
        </div>
      )}

      {/* Action Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={handleCreateClick}
          className="flex items-center justify-center gap-2 bg-emerald-500 text-white font-semibold py-3 rounded-xl hover:bg-emerald-600 transition-all"
          data-testid="create-league-btn"
        >
          <Plus size={18} />
          Create League
        </button>
        <button
          onClick={() => user ? setShowJoinModal(true) : navigate("/auth", { state: { returnTo: "/leagues" } })}
          className="flex items-center justify-center gap-2 bg-slate-700 text-white font-semibold py-3 rounded-xl hover:bg-slate-600 transition-all border border-slate-600"
          data-testid="join-league-btn"
        >
          <LogIn size={18} />
          Join with Code
        </button>
      </div>

      {/* My Leagues Section */}
      {user && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold text-white">My Leagues</h2>
            <div className="flex items-center gap-3 text-xs">
              <span className="text-slate-500">Created: {slots.create_used}/{slots.create_available}</span>
              <span className="text-slate-500">Joined: {slots.join_used}/{slots.join_limit}</span>
            </div>
          </div>
          
          {loadingMy ? (
            <div className="flex justify-center py-8">
              <Loader2 className="animate-spin text-emerald-400" size={24} />
            </div>
          ) : myLeagues.length === 0 ? (
            <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6 text-center">
              <Crown className="mx-auto text-slate-600 mb-3" size={32} />
              <p className="text-slate-400 text-sm">No leagues yet</p>
              <p className="text-slate-500 text-xs mt-1">Create or join a league to get started</p>
            </div>
          ) : (
            <div className="space-y-2">
              {myLeagues.map((league) => (
                <div 
                  key={league.id}
                  className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-3 hover:border-slate-600 transition-all cursor-pointer"
                  onClick={() => navigate(`/my-leagues/${league.id}`)}
                  data-testid={`my-league-${league.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500 flex items-center justify-center">
                        <Crown className="text-white" size={18} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-semibold text-white text-sm">{league.name}</h3>
                          {league.visibility === "public" ? (
                            <Globe size={12} className="text-emerald-400" />
                          ) : (
                            <Lock size={12} className="text-slate-500" />
                          )}
                          {league.is_creator && (
                            <Crown size={12} className="text-amber-400" />
                          )}
                        </div>
                        <p className="text-slate-400 text-xs flex items-center gap-1">
                          <Users size={12} />
                          {league.member_count} members
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={(e) => { e.stopPropagation(); copyCode(league.code); }}
                        className="p-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg transition-all"
                        title="Copy code"
                      >
                        <Copy size={14} className="text-slate-300" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); shareWhatsApp(league.code, league.name); }}
                        className="p-1.5 bg-green-600 hover:bg-green-500 rounded-lg transition-all"
                        title="Share"
                      >
                        <Share2 size={14} className="text-white" />
                      </button>
                      <ChevronRight className="text-slate-500" size={18} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Public Leagues Section */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-white flex items-center gap-2">
            <Globe className="text-emerald-400" size={18} />
            Public Leagues
          </h2>
          <span className="text-slate-500 text-xs">{totalPublic} available</span>
        </div>

        {/* Search */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
            <input
              type="text"
              placeholder="Search leagues..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 text-sm focus:outline-none focus:border-emerald-500"
              data-testid="search-public-leagues"
            />
          </div>
          <button 
            type="submit" 
            className="px-3 py-2 bg-emerald-600 rounded-lg text-sm font-medium hover:bg-emerald-500 transition-all"
          >
            Search
          </button>
        </form>

        {/* Sort */}
        <div className="flex gap-2 mb-3">
          <button
            onClick={() => { setSort("members"); setPage(1); }}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
              sort === "members" ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-300"
            }`}
          >
            Most Members
          </button>
          <button
            onClick={() => { setSort("created_at"); setPage(1); }}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-all ${
              sort === "created_at" ? "bg-emerald-500 text-white" : "bg-slate-700 text-slate-300"
            }`}
          >
            Newest
          </button>
        </div>

        {/* Public Leagues List */}
        {loadingPublic ? (
          <div className="flex justify-center py-8">
            <Loader2 className="animate-spin text-emerald-400" size={24} />
          </div>
        ) : publicLeagues.length === 0 ? (
          <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-6 text-center">
            <Globe className="mx-auto text-slate-600 mb-3" size={32} />
            <p className="text-slate-400 text-sm">No public leagues found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {publicLeagues.map(league => {
              const isMember = user && myLeagues.some(l => l.id === league.id);
              
              return (
                <div 
                  key={league.id}
                  className={`p-3 rounded-xl border transition-all cursor-pointer ${
                    league.is_full 
                      ? "bg-slate-800/30 border-slate-700/50 opacity-60" 
                      : "bg-slate-800/50 border-slate-700 hover:border-slate-600"
                  }`}
                  onClick={() => handleLeagueClick(league)}
                  data-testid={`public-league-${league.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-white text-sm">{league.name}</h3>
                        {league.is_full && (
                          <span className="px-1.5 py-0.5 bg-slate-600 text-slate-300 rounded text-xs">Full</span>
                        )}
                        {isMember && (
                          <span className="px-1.5 py-0.5 bg-emerald-600 text-white rounded text-xs">Joined</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 text-xs text-slate-400">
                        <Crown size={12} className="text-amber-400" />
                        <span>{league.organizer_name}</span>
                        <span className="text-slate-600">•</span>
                        <Users size={12} />
                        <span>{league.member_count}/{league.max_members}</span>
                      </div>
                    </div>
                    <ChevronRight className="text-slate-500" size={18} />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 pt-4">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-400 disabled:opacity-50 text-sm"
            >
              Previous
            </button>
            <span className="text-slate-400 text-sm">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 rounded-lg bg-slate-800 text-slate-400 disabled:opacity-50 text-sm"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Engagement Lock Modal */}
      <EngagementLockModal
        open={showEngagementModal}
        onClose={() => setShowEngagementModal(false)}
        status={engagement}
      />

      {/* Create League Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Create League</h2>
              <button onClick={() => setShowCreateModal(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={createLeague} className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">League Name *</label>
                <input
                  type="text"
                  value={newLeagueName}
                  onChange={(e) => setNewLeagueName(e.target.value)}
                  placeholder="e.g., Office Champions"
                  className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500"
                  data-testid="league-name-input"
                  autoFocus
                  maxLength={50}
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Description (optional)</label>
                <textarea
                  value={newLeagueDescription}
                  onChange={(e) => setNewLeagueDescription(e.target.value)}
                  placeholder="Tell people about your league..."
                  className="w-full px-4 py-2.5 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500 resize-none"
                  rows={2}
                  maxLength={500}
                />
              </div>

              <div>
                <label className="block text-sm text-slate-400 mb-1">Visibility</label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setNewLeagueVisibility("private")}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                      newLeagueVisibility === "private" 
                        ? "bg-emerald-600 text-white" 
                        : "bg-slate-700 text-slate-300"
                    }`}
                  >
                    <Lock size={14} />
                    Private
                  </button>
                  <button
                    type="button"
                    onClick={() => setNewLeagueVisibility("public")}
                    className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-all ${
                      newLeagueVisibility === "public" 
                        ? "bg-emerald-600 text-white" 
                        : "bg-slate-700 text-slate-300"
                    }`}
                  >
                    <Globe size={14} />
                    Public
                  </button>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={creating}
                className="w-full bg-emerald-500 text-white font-semibold py-3 rounded-lg hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="create-league-submit"
              >
                {creating ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />}
                {creating ? "Creating..." : "Create League"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Join League Modal */}
      {showJoinModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Join League</h2>
              <button onClick={() => setShowJoinModal(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <form onSubmit={joinLeague} className="space-y-4">
              <div>
                <label className="block text-sm text-slate-400 mb-1">League Code</label>
                <input
                  type="text"
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  placeholder="ML-XXXXXX"
                  className="w-full px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-emerald-500 font-mono text-center text-lg tracking-wider"
                  data-testid="join-code-input"
                  autoFocus
                />
              </div>
              
              <button
                type="submit"
                disabled={joining}
                className="w-full bg-emerald-500 text-white font-semibold py-3 rounded-lg hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                data-testid="join-league-submit"
              >
                {joining ? <Loader2 className="animate-spin" size={18} /> : <LogIn size={18} />}
                {joining ? "Joining..." : "Join League"}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* League Preview Modal */}
      {showPreviewModal && previewLeague && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md" data-testid="league-preview-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">{previewLeague.name}</h2>
              <button onClick={() => setShowPreviewModal(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Organizer */}
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center">
                  <Crown className="text-white" size={20} />
                </div>
                <div>
                  <p className="text-slate-400 text-xs">Organized by</p>
                  <p className="text-white font-medium">{previewLeague.organizer_name}</p>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                  <p className="text-slate-400 text-xs">Members</p>
                  <p className="text-white font-bold text-lg">{previewLeague.member_count}/{previewLeague.max_members}</p>
                </div>
                <div className="bg-slate-900/50 rounded-lg p-3 text-center">
                  <p className="text-slate-400 text-xs">Type</p>
                  <p className="text-emerald-400 font-bold text-lg flex items-center justify-center gap-1">
                    <Globe size={16} />
                    Public
                  </p>
                </div>
              </div>
              
              {/* Description */}
              {previewLeague.description && (
                <div className="bg-slate-900/50 rounded-lg p-3">
                  <p className="text-slate-400 text-xs mb-1">About</p>
                  <p className="text-slate-200 text-sm">{previewLeague.description}</p>
                </div>
              )}
              
              {/* Join Button */}
              {previewLeague.is_full ? (
                <div className="bg-slate-700 text-slate-400 py-3 rounded-lg text-center font-medium">
                  League is Full
                </div>
              ) : (
                <button
                  onClick={() => requestToJoin(previewLeague.id, previewLeague.name)}
                  disabled={requestingId === previewLeague.id}
                  className="w-full bg-emerald-500 text-white font-semibold py-3 rounded-lg hover:bg-emerald-600 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="request-join-btn"
                >
                  {requestingId === previewLeague.id ? (
                    <Loader2 className="animate-spin" size={18} />
                  ) : (
                    <LogIn size={18} />
                  )}
                  Request to Join
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upgrade Modal with RevenueCat */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6 w-full max-w-md" data-testid="upgrade-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">Upgrade Leagues</h2>
              <button onClick={() => setShowUpgradeModal(false)} className="text-slate-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            
            <div className="space-y-3">
              <p className="text-slate-400 text-sm">
                You've used all your free slots. Purchase additional league slots:
              </p>

              {/* Private League */}
              <button
                onClick={() => isNative && rcInitialized ? handlePurchase("private") : null}
                disabled={purchasing === "private" || (!isNative || !rcInitialized)}
                className="w-full p-4 bg-slate-700/50 border border-slate-600 rounded-lg text-left hover:border-emerald-500 transition-all disabled:opacity-60"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white font-medium flex items-center gap-2">
                      <Lock size={16} />
                      Extra Private League
                    </p>
                    <p className="text-slate-400 text-sm">Create one additional private league</p>
                  </div>
                  <div className="text-right">
                    <span className="text-emerald-400 font-bold">AED 5</span>
                    {purchasing === "private" && <Loader2 className="animate-spin ml-2 inline" size={16} />}
                  </div>
                </div>
              </button>

              {/* Public League */}
              <button
                onClick={() => isNative && rcInitialized ? handlePurchase("public") : null}
                disabled={purchasing === "public" || (!isNative || !rcInitialized)}
                className="w-full p-4 bg-slate-700/50 border border-slate-600 rounded-lg text-left hover:border-emerald-500 transition-all disabled:opacity-60"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white font-medium flex items-center gap-2">
                      <Globe size={16} />
                      Public League Slot
                    </p>
                    <p className="text-slate-400 text-sm">Create one public league visible to all</p>
                  </div>
                  <div className="text-right">
                    <span className="text-emerald-400 font-bold">AED 10</span>
                    {purchasing === "public" && <Loader2 className="animate-spin ml-2 inline" size={16} />}
                  </div>
                </div>
              </button>

              {/* Size Upgrades */}
              <button
                onClick={() => isNative && rcInitialized ? handlePurchase("size_15") : null}
                disabled={purchasing === "size_15" || (!isNative || !rcInitialized)}
                className="w-full p-4 bg-slate-700/50 border border-slate-600 rounded-lg text-left hover:border-emerald-500 transition-all disabled:opacity-60"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white font-medium flex items-center gap-2">
                      <Users size={16} />
                      Upgrade to 15 Members
                    </p>
                    <p className="text-slate-400 text-sm">Expand league capacity to 15</p>
                  </div>
                  <div className="text-right">
                    <span className="text-emerald-400 font-bold">AED 5</span>
                    {purchasing === "size_15" && <Loader2 className="animate-spin ml-2 inline" size={16} />}
                  </div>
                </div>
              </button>

              <button
                onClick={() => isNative && rcInitialized ? handlePurchase("size_30") : null}
                disabled={purchasing === "size_30" || (!isNative || !rcInitialized)}
                className="w-full p-4 bg-slate-700/50 border border-slate-600 rounded-lg text-left hover:border-blue-500 transition-all disabled:opacity-60"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white font-medium flex items-center gap-2">
                      <Users size={16} />
                      Upgrade to 30 Members
                    </p>
                    <p className="text-slate-400 text-sm">Expand league capacity to 30</p>
                  </div>
                  <div className="text-right">
                    <span className="text-blue-400 font-bold">AED 10</span>
                    {purchasing === "size_30" && <Loader2 className="animate-spin ml-2 inline" size={16} />}
                  </div>
                </div>
              </button>

              <button
                onClick={() => isNative && rcInitialized ? handlePurchase("size_50") : null}
                disabled={purchasing === "size_50" || (!isNative || !rcInitialized)}
                className="w-full p-4 bg-slate-700/50 border border-slate-600 rounded-lg text-left hover:border-purple-500 transition-all disabled:opacity-60"
              >
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-white font-medium flex items-center gap-2">
                      <Users size={16} />
                      Upgrade to 50 Members
                    </p>
                    <p className="text-slate-400 text-sm">Maximum league capacity</p>
                  </div>
                  <div className="text-right">
                    <span className="text-purple-400 font-bold">AED 15</span>
                    {purchasing === "size_50" && <Loader2 className="animate-spin ml-2 inline" size={16} />}
                  </div>
                </div>
              </button>

              {/* Platform Notice */}
              {!isNative && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="text-amber-400 flex-shrink-0 mt-0.5" size={16} />
                  <div>
                    <p className="text-amber-200 text-sm font-medium">Purchases available in the mobile app</p>
                    <p className="text-amber-300/70 text-xs mt-1">
                      Download APL from App Store or Google Play to purchase upgrades.
                    </p>
                  </div>
                </div>
              )}

              {isNative && !rcInitialized && (
                <div className="bg-slate-700/50 rounded-lg p-3 flex items-center justify-center gap-2 text-slate-400">
                  <Loader2 className="animate-spin" size={16} />
                  <span className="text-sm">Loading purchase options...</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
    </div>
  );
}
